import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, ArrowRight, CheckCircle2 } from "lucide-react";
import { useI18n, useLocalizedPath } from "../i18n/I18nContext";
import { api, formatApiError } from "../lib/api";

export default function Footer() {
  const { t, lang } = useI18n();
  const lp = useLocalizedPath();
  const year = new Date().getFullYear();

  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | loading | success | error
  const [err, setErr] = useState("");

  const onSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setState("loading");
    try {
      await api.post("/newsletter/subscribe", { email: email.trim(), language: lang });
      setState("success");
      setEmail("");
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
      setState("error");
    }
  };

  return (
    <footer className="relative mt-32 border-t border-white/5">
      <div className="container py-20 md:py-28">
        {/* Newsletter band */}
        <div className="glass rounded-3xl p-8 md:p-12 mb-20 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-6">
            <p className="font-mono-label text-gold/80 mb-4">{t.newsletter.kicker}</p>
            <h3 className="font-display text-section tracking-tight">{t.newsletter.title}</h3>
            <p className="mt-3 text-cream-muted">{t.newsletter.sub}</p>
          </div>
          <div className="md:col-span-6">
            {state === "success" ? (
              <div className="flex items-start gap-3 text-cream">
                <CheckCircle2 className="h-5 w-5 text-gold mt-0.5 shrink-0" />
                <p className="text-small">{t.newsletter.success}</p>
              </div>
            ) : (
              <form onSubmit={onSubscribe} className="flex flex-col sm:flex-row gap-3">
                <div className="flex items-center gap-3 input-field !py-3 focus-within:!border-gold/55 flex-1">
                  <Mail className="h-4 w-4 text-cream-dim" />
                  <input
                    data-testid="newsletter-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t.newsletter.placeholder}
                    className="input-bare"
                  />
                </div>
                <button
                  data-testid="newsletter-submit"
                  type="submit"
                  disabled={state === "loading"}
                  className="btn btn-gold disabled:opacity-50"
                >
                  {state === "loading" ? t.common.sending : t.newsletter.submit}
                  <ArrowRight className="h-4 w-4" />
                </button>
              </form>
            )}
            {state === "error" && <p className="mt-3 text-caption text-red-300/90">{err}</p>}
          </div>
        </div>

        {/* Sitemap rows */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8">
          <div className="md:col-span-5">
            <Link to={lp("/")} className="inline-flex items-baseline gap-1">
              <span className="font-display text-card-lg font-bold">The Life</span>
              <span className="ed-italic text-card-lg text-gold">Plot</span>
            </Link>
            <p className="mt-5 ed-italic text-subtitle text-cream-muted max-w-sm">
              {t.footer.tag}
            </p>
            <p className="mt-6 text-small text-cream-dim max-w-sm">{t.mission}</p>
            <p className="mt-6 text-small">
              <a href="mailto:hello@thelifeplot.com" className="text-gold hover:text-gold-hover transition-colors">
                hello@thelifeplot.com
              </a>
            </p>
          </div>

          <div className="md:col-span-2">
            <h4 className="font-mono-label text-cream-dim mb-5">{t.footer.explore}</h4>
            <ul className="space-y-3">
              <li><Link className="text-small text-cream-muted hover:text-cream" to={lp("/guides")}>{t.nav.guides}</Link></li>
              <li><Link className="text-small text-cream-muted hover:text-cream" to={lp("/assessment")}>{t.nav.assessment}</Link></li>
              <li><Link className="text-small text-cream-muted hover:text-cream" to={lp("/blog")}>{t.nav.blog}</Link></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <h4 className="font-mono-label text-cream-dim mb-5">{t.footer.company}</h4>
            <ul className="space-y-3">
              <li><Link className="text-small text-cream-muted hover:text-cream" to={lp("/contact")}>{t.footer.contact}</Link></li>
              <li>
                <a className="text-small text-cream-muted hover:text-cream" href="mailto:hello@thelifeplot.com">
                  hello@thelifeplot.com
                </a>
              </li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <h4 className="font-mono-label text-cream-dim mb-5">{t.footer.legal}</h4>
            <ul className="space-y-3">
              <li><Link className="text-small text-cream-muted hover:text-cream" to={lp("/legal/privacy")}>{t.footer.privacy}</Link></li>
              <li><Link className="text-small text-cream-muted hover:text-cream" to={lp("/legal/terms")}>{t.footer.terms}</Link></li>
            </ul>
          </div>
        </div>

        <div className="divider-fade mt-16 mb-6" />

        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-caption font-mono-label text-cream-dim">
          <p>© {year} The Life Plot — {t.footer.rights}</p>
          <p>thelifeplot.com</p>
        </div>
      </div>
    </footer>
  );
}
